import { FC, useEffect, useState } from 'react';
import Typography from '@components/common/typography';
import styles from './styles.module.scss';
import { useUserContext } from '@hooks/context/useUserContext';
import { IModalComponentProps } from '@context/modal_context';
import { prettifyAmount } from '@utils/index';
import { useForm } from '@hooks/helpers/useForm';
import { useLocales } from '@hooks/helpers/useLocales';
import { FormProvider } from 'react-hook-form';
import { TGiftCard, TGiftCardListItem } from '@xeppt/xeppt-sdk/types';
import { Icon } from '@components/icons';

const ActivateGiftCardModal: FC<
    IModalComponentProps<{
        giftCards: (TGiftCard | TGiftCardListItem)[];
        refetchGiftCards?: () => void;
        isUploaded?: boolean;
    }>
> = ({ modalData, changeModalData, handleChangeDisable }) => {
    const { account } = useUserContext();
    const [isInsufficient, setIsInsufficient] = useState(false);
    const { validationLocale, placeholderLocale } = useLocales();
    const form = useForm({
        defaultValues: {
            amount: '',
            reference: ''
        }
    });
    const { giftCards, isUploaded } = modalData;

    useEffect(() => {
        const totalAmount = giftCards.reduce((acc, card) => acc + card.amount, 0);
        if (account && totalAmount > account.balance) {
            handleChangeDisable(true);
            setIsInsufficient(true);
        } else {
            handleChangeDisable(false);
            setIsInsufficient(false);
        }
    }, [account, giftCards]);

    return (
        <FormProvider {...form}>
            <div className={styles.wrapper}>
                <Typography className={styles.title} variant="h4">
                    Activate gift card
                </Typography>
                <div className={styles.balance_wrapper}>
                    <div className={styles.balance}>
                        <Typography variant="body3">
                            Current XEPPT wallet balance: $
                            <b>{prettifyAmount((account?.balance || 0).toFixed(2) || '')}</b>
                        </Typography>
                    </div>
                    {isInsufficient && (
                        <div className={styles.insufficient_warning}>
                            <Icon name="filled_warning" />{' '}
                            <Typography variant="body4">Insufficient balance</Typography>
                        </div>
                    )}
                </div>
                <div className={styles.summary}>
                    <div className={styles.block}>
                        <Typography variant="body3" fw={600} className={styles.title}>
                            Gift cards details
                        </Typography>
                        {giftCards.length > 1 ? (
                            <div className={styles.content}>
                                <div className={`${styles.row} ${styles.map}`}>
                                    <Typography variant="body3" className={styles.semititle}>
                                        Gift card numbers and values
                                    </Typography>
                                    <div>
                                        {giftCards.map((item) => (
                                            <Typography variant="body4" key={item.id}>
                                                {item.gift_card_serial_number} - $
                                                {prettifyAmount(item.amount.toFixed(2))}
                                                {item.currency}
                                            </Typography>
                                        ))}
                                    </div>
                                </div>
                                <div className={styles.row}>
                                    <Typography variant="body4">Quantity of gift cards</Typography>
                                    <div>{giftCards.length}</div>
                                </div>
                                <div className={styles.row}>
                                    <Typography variant="body4">
                                        Amount that will be reserved
                                    </Typography>
                                    <span className={styles.bold}>
                                        ${giftCards.reduce((a, b) => a + b.amount, 0).toFixed(2)}CAD
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <div className={styles.content}>
                                <div className={styles.row}>
                                    <Typography variant="body3">Gift card number</Typography>
                                    <div>
                                        {giftCards.map((item) => (
                                            <Typography variant="body4" key={item.id}>
                                                {item.gift_card_serial_number}
                                            </Typography>
                                        ))}
                                    </div>
                                </div>
                                <div className={styles.row}>
                                    <Typography variant="body4">Gift card value</Typography>
                                    <div>
                                        ${giftCards.reduce((a, b) => a + b.amount, 0).toFixed(2)}{' '}
                                        CAD
                                    </div>
                                </div>
                                <div className={styles.row}>
                                    <Typography variant="body4">
                                        Amount that will be reserved
                                    </Typography>
                                    <span className={styles.bold}>
                                        ${giftCards.reduce((a, b) => a + b.amount, 0).toFixed(2)}{' '}
                                        CAD
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                    {(isUploaded || giftCards.length === 1) && <div className={styles.divider} />}
                    {(isUploaded || giftCards.length === 1) && (
                        <div className={styles.block}>
                            <Typography variant="body3" fw={600} className={styles.title}>
                                Recipients details
                            </Typography>
                            <div className={styles.row}>
                                <Typography variant="body4">Recipient email</Typography>
                                <span>
                                    {isUploaded
                                        ? 'From uploaded CSV'
                                        : giftCards?.length > 1
                                          ? 'Bulk activation'
                                          : giftCards[0]?.email
                                            ? giftCards[0]?.email
                                            : giftCards[0]?.phone_code && giftCards[0]?.phone_number
                                              ? `${giftCards[0].phone_code?.includes('+') ? giftCards[0].phone_code : '+' + giftCards[0].phone_code} ${giftCards[0].phone_number}`
                                              : '-'}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </FormProvider>
    );
};

export default ActivateGiftCardModal;
